// WHAT: CRUD Service für Context Intelligence Insights
// WHY:  Zentrale Datenzugriffsschicht für Insights in Supabase
// DOMAIN: core → context-intelligence
// DEPENDENCIES: @tracq/frontend-shared/services (Supabase Client)
// RISKS:
//   - Tabelle existiert möglicherweise noch nicht (Migration)
//   - Network-Fehler werden stillschweigend abgefangen
//   - Hohe Cognitive Complexity in getActiveInsights (reduziert durch Query-Builder)

import type {
  Insight,
  InsightQuery,
  InsightEvaluation,
  InsightSource,
} from "../types/insights.types";
import type { PostgrestError } from "@supabase/supabase-js";

import { getSupabaseClient } from "@/shared/services";

interface DatabaseInsightRow {
  category: Insight["category"];
  confidence: number;
  description: string;
  generated_at: string;
  id: string;
  indicator_ids: string[];
  metadata: null | Record<string, unknown>;
  priority: Insight["priority"];
  relationship_ids: null | string[];
  relevance: number;
  sources: unknown;
  status: Insight["status"];
  title: string;
  valid_until: null | string;
  version: number;
}

interface DatabaseEvaluationRow {
  confidence: number;
  evaluated_at: string;
  evaluator: string;
  evidence: Record<string, unknown>;
  id: string;
  insight_id: string;
  insight_version: number;
  notes: null | string;
  relevance: number;
  still_valid: boolean;
}

/**
 * WHAT:
 *   Baut einen Supabase Query-Builder für Insight-Abfragen auf.
 *
 * WHY:
 *   Reduziert Cognitive Complexity von getActiveInsights durch Extraktion
 *   der Query-Builder-Logik in eine separate Funktion.
 *
 * @param queryBuilder - Basis Query-Builder von Supabase
 *
 * @param query - Optionale Query-Parameter für Filterung
 *
 * @returns Konfigurierter Query-Builder mit allen angewendeten Filtern.
 *
 * SIDE EFFECTS:
 *   Keine - reine Transformation des Query-Builders.
 *
 */
/**
 * Baut einen Supabase Query-Builder für Insight-Abfragen auf.
 *
 * @param queryBuilder - Basis Query-Builder von Supabase
 *
 * @param query - Optionale Query-Parameter für Filterung
 *
 * @returns Konfigurierter Query-Builder mit allen angewendeten Filtern.
 *
 */
/* eslint-disable @typescript-eslint/no-explicit-any -- Reason: Supabase Query-Builder hat komplexe generische Typen mit Method-Chaining, die nicht einfach typisiert werden können. Die any-Typen sind notwendig für die Method-Chaining-API. */
// eslint-disable-next-line jsdoc/require-jsdoc -- Reason: JSDoc ist bereits oben definiert, eslint-disable steht dazwischen
function buildInsightQuery(queryBuilder: any, query?: InsightQuery): any {
  let builder = queryBuilder;

  if (!query) {
    return builder;
  }

  if (query.priority) {
    const priorities = Array.isArray(query.priority) ? query.priority : [query.priority];
    builder = builder.in("priority", priorities);
  }

  if (query.category) {
    const categories = Array.isArray(query.category) ? query.category : [query.category];
    builder = builder.in("category", categories);
  }

  if (query.minRelevance !== undefined) {
    builder = builder.gte("relevance", query.minRelevance);
  }

  if (query.minConfidence !== undefined) {
    builder = builder.gte("confidence", query.minConfidence);
  }

  if (query.dateRange) {
    builder = builder
      .gte("generated_at", query.dateRange.start.toISOString())
      .lte("generated_at", query.dateRange.end.toISOString());
  }

  if (query.limit !== undefined) {
    builder = builder.limit(query.limit);
  }

  if (query.offset !== undefined) {
    const limit = query.limit ?? 10;
    builder = builder.range(query.offset, query.offset + limit - 1);
  }

  return builder;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * WHAT:
 *   Sortiert Insights nach Generierungszeit (neueste zuerst) und dann nach Relevanz.
 *
 * WHY:
 *   Zentrale Sortierlogik für konsistente Reihenfolge.
 *
 * @param insights - Array von Insights zum Sortieren
 *
 * @returns void (mutiert das Input-Array).
 *
 * SIDE EFFECTS:
 *   Mutiert das Input-Array.
 *
 */
function sortInsightsByDateAndRelevance(insights: Insight[]): void {
  insights.sort((a, b) => {
    // First sort by generatedAt (newest first)
    const timeDiff = b.generatedAt - a.generatedAt;
    if (timeDiff !== 0) {
      return timeDiff;
    }
    // If same time, sort by relevance (higher first)
    return b.relevance - a.relevance;
  });
}

/**
 * WHAT:
 *   Mappt einen Datenbank-Record zu einem Insight-Objekt.
 *
 * WHY:
 *   Transformation zwischen Datenbank-Schema (snake_case) und Domain-Modell (camelCase).
 *
 * @param databaseRecord - Datenbank-Record mit snake_case Feldern
 *
 * @returns Insight-Objekt mit camelCase Feldern.
 *
 * SIDE EFFECTS:
 *   Keine.
 *
 */
function mapDbToInsight(databaseRecord: DatabaseInsightRow): Insight {
  return {
    category: databaseRecord.category,
    confidence: databaseRecord.confidence,
    description: databaseRecord.description,
    generatedAt: new Date(databaseRecord.generated_at).getTime(),
    id: databaseRecord.id,
    indicatorIds: databaseRecord.indicator_ids,
    metadata: databaseRecord.metadata ?? {},
    priority: databaseRecord.priority,
    relationshipIds: databaseRecord.relationship_ids ?? [],
    relevance: databaseRecord.relevance,
    sources: databaseRecord.sources as InsightSource[],
    status: databaseRecord.status,
    title: databaseRecord.title,
    ...(databaseRecord.valid_until && {
      validUntil: new Date(databaseRecord.valid_until).getTime(),
    }),
    version: databaseRecord.version,
  };
}

/**
 * WHAT:
 *   Findet ähnliche Insights (gleicher Titel, aktiver Status).
 *
 * WHY:
 *   Unterstützt Versionierung durch Identifikation von Duplikaten vor dem Erstellen.
 *
 * @param insight - Insight-Daten ohne generierte Felder
 *
 * @returns Gefundenes ähnliches Insight oder null.
 *
 * SIDE EFFECTS:
 *   Datenbankabfrage.
 *
 */
async function findSimilarInsight(
  insight: Omit<Insight, "generatedAt" | "id" | "version">,
): Promise<Insight | null> {
  const supabase = await getSupabaseClient();

  // Find insights with same title and indicator IDs
  const { data } = (await supabase
    .from("context_insights")
    .select("*")
    .eq("title", insight.title)
    .eq("status", "active")
    .order("version", { ascending: false })
    .limit(1)) as {
    data: DatabaseInsightRow[] | null;
  };

  if (data && data.length > 0 && data[0] !== undefined) {
    return mapDbToInsight(data[0]);
  }

  return null;
}

/**
 * WHAT:
 *   CRUD Service für Context Intelligence Insights.
 *   Handles CRUD operations for insights in Supabase.
 *
 * WHY:
 *   Zentrale Service-Schicht für alle Insight-Operationen.
 *   Objekt-basiertes Pattern statt statischer Klasse für bessere TypeScript-Idiomatik.
 */
export const insightService = {
  /**
   * WHAT:
   *   Ruft aktive Insights ab, optional gefiltert nach Query-Parametern.
   *
   * WHY:
   *   Hauptabfrage-Methode für Insights mit flexibler Filterung.
   *
   * @param query - Optionale Query-Parameter für Filterung, Pagination, etc.
   *
   * @returns Promise mit Array von Insights, sortiert nach Datum (neueste zuerst) und Relevanz.
   *
   * SIDE EFFECTS:
   *   Datenbankabfrage. Bei Fehlern wird leeres Array zurückgegeben (graceful degradation).
   *
   * RISKS:
   *   - Tabelle existiert möglicherweise noch nicht (Migration) → leeres Array
   *   - Network-Fehler werden stillschweigend abgefangen → leeres Array
   *
   */
  async getActiveInsights(query?: InsightQuery): Promise<Insight[]> {
    try {
      const supabase = await getSupabaseClient();
      let queryBuilder = supabase
        .from("context_insights")
        .select("*")
        .eq("status", "active")
        .order("generated_at", { ascending: false });

      queryBuilder = buildInsightQuery(queryBuilder, query);

      const { data, error } = (await queryBuilder) as {
        data: DatabaseInsightRow[] | null;
        error: null | PostgrestError;
      };

      if (error) {
        // If table doesn't exist yet, return empty array (migration not run)
        if (error.code === "42P01" || error.message.includes("does not exist")) {
          return [];
        }
        throw new Error(`Failed to fetch insights: ${error.message}`);
      }

      // Map to Insight objects and sort: newest first, then by relevance
      const insights = (data ?? []).map((row) => mapDbToInsight(row));

      // Sort: newest first (by generatedAt), then by relevance
      sortInsightsByDateAndRelevance(insights);

      return insights;
    } catch {
      // Handle network errors or other issues gracefully by returning an empty list
      return [];
    }
  },

  /**
   * WHAT:
   *   Ruft Insights nach Kategorie ab.
   *
   * WHY:
   *   Convenience-Methode für kategorie-basierte Abfragen.
   *
   * @param category - Kategorie der Insights
   *
   * @returns Promise mit Array von Insights der angegebenen Kategorie.
   *
   * SIDE EFFECTS:
   *   Datenbankabfrage über getActiveInsights.
   *
   */
  async getInsightsByCategory(category: Insight["category"]): Promise<Insight[]> {
    return this.getActiveInsights({ category });
  },

  /**
   * WHAT:
   *   Ruft die Versionshistorie eines Insights ab (alle Versionen).
   *
   * WHY:
   *   Ermöglicht Nachverfolgung von Insight-Änderungen über die Zeit.
   *
   * @param insightId - ID des Insights
   *
   * @returns Promise mit Array aller Versionen des Insights, sortiert nach Version (neueste zuerst).
   *
   * SIDE EFFECTS:
   *   Datenbankabfrage.
   *
   * RISKS:
   *   Wirft Fehler bei Datenbankfehlern (kein graceful degradation).
   *
   */
  async getInsightHistory(insightId: string): Promise<Insight[]> {
    const supabase = await getSupabaseClient();
    const { data, error } = (await supabase
      .from("context_insights")
      .select("*")
      .eq("id", insightId)
      .order("version", { ascending: false })) as {
      data: DatabaseInsightRow[] | null;
      error: null | PostgrestError;
    };

    if (error) {
      throw new Error(`Failed to fetch insight history: ${error.message}`);
    }

    return (data ?? []).map((row) => mapDbToInsight(row));
  },

  /**
   * WHAT:
   *   Erstellt ein neues Insight.
   *
   * WHY:
   *   Zentrale Methode für Insight-Erstellung mit automatischer Duplikat-Erkennung.
   *
   * NOTE:
   *   Since `id` is the PRIMARY KEY, we always generate a new unique ID.
   *   If a similar insight exists, we archive the old one and create a new insight.
   *
   * @param insight - Insight-Daten ohne generierte Felder (generatedAt, id, version)
   *
   * @returns Promise mit dem erstellten Insight.
   *
   * SIDE EFFECTS:
   *   - Datenbankabfrage (findSimilarInsight)
   *   - Datenbank-Update (Archivierung ähnlicher Insights)
   *   - Datenbank-Insert (neues Insight)
   *
   * RISKS:
   *   - Wirft Fehler bei Datenbankfehlern
   *   - Archiviert alle Insights mit gleichem Titel (nicht nur das gefundene)
   *
   */
  async createInsight(insight: Omit<Insight, "generatedAt" | "id" | "version">): Promise<Insight> {
    const supabase = await getSupabaseClient();

    // Check if insight with similar content already exists
    // If so, archive ALL old insights with the same title before creating a new one
    const existing = await findSimilarInsight(insight);

    if (existing) {
      // Archive ALL insights with the same title (not just the one with the same ID)
      // This ensures we don't have duplicate active insights with the same title
      await supabase
        .from("context_insights")
        .update({ status: "archived", updated_at: new Date().toISOString() })
        .eq("title", insight.title)
        .eq("status", "active");
    }

    // Always create a new insight with a new unique ID
    // Version is always 1 since each insight has its own unique ID
    const insightId = crypto.randomUUID();
    const version = 1;

    const databaseInsight = {
      category: insight.category,
      confidence: insight.confidence,
      description: insight.description,
      generated_at: new Date().toISOString(),
      id: insightId,
      indicator_ids: insight.indicatorIds,
      metadata: {
        ...insight.metadata,
        // Store reference to previous insight if it was an update
        ...(existing && {
          previousInsightId: existing.id,
          previousVersion: existing.version,
        }),
      },
      priority: insight.priority,
      relationship_ids: insight.relationshipIds ?? [],
      relevance: insight.relevance,
      sources: insight.sources,
      status: insight.status,
      title: insight.title,
      valid_until: insight.validUntil ? new Date(insight.validUntil).toISOString() : null,
      version,
    };

    const { data, error } = (await supabase
      .from("context_insights")
      .insert(databaseInsight)
      .select()
      .single()) as {
      data: DatabaseInsightRow | null;
      error: null | PostgrestError;
    };

    if (error) {
      throw new Error(`Failed to create insight: ${error.message}`);
    }

    if (!data) {
      throw new Error("Failed to create insight: No data returned");
    }

    return mapDbToInsight(data);
  },

  /**
   * WHAT:
   *   Aktualisiert den Status eines Insights.
   *
   * WHY:
   *   Zentrale Methode für Status-Änderungen (active, archived, etc.).
   *
   * @param insightId - ID des Insights
   *
   * @param version - Version des Insights
   *
   * @param status - Neuer Status
   *
   * @returns Promise ohne Rückgabewert (void).
   *
   * SIDE EFFECTS:
   *   Datenbank-Update.
   *
   * RISKS:
   *   Wirft Fehler bei Datenbankfehlern.
   *
   */
  async updateInsightStatus(
    insightId: string,
    version: number,
    status: Insight["status"],
  ): Promise<void> {
    const supabase = await getSupabaseClient();
    const { error } = await supabase
      .from("context_insights")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", insightId)
      .eq("version", version);

    if (error) {
      throw new Error(`Failed to update insight status: ${error.message}`);
    }
  },

  /**
   * WHAT:
   *   Archiviert ein Insight.
   *
   * WHY:
   *   Convenience-Methode für Archivierung.
   *
   * @param insightId - ID des Insights
   *
   * @param version - Version des Insights
   *
   * @returns Promise ohne Rückgabewert (void).
   *
   * SIDE EFFECTS:
   *   Datenbank-Update über updateInsightStatus.
   *
   */
  async archiveInsight(insightId: string, version: number): Promise<void> {
    await this.updateInsightStatus(insightId, version, "archived");
  },

  /**
   * WHAT:
   *   Erstellt eine Insight-Evaluierung.
   *
   * WHY:
   *   Ermöglicht Validierung und Bewertung von Insights über die Zeit.
   *
   * @param evaluation - Evaluierungs-Daten ohne generierte Felder (evaluatedAt, id)
   *
   * @returns Promise mit der erstellten Evaluierung.
   *
   * SIDE EFFECTS:
   *   Datenbank-Insert.
   *
   * RISKS:
   *   Wirft Fehler bei Datenbankfehlern.
   *
   */
  async createEvaluation(
    evaluation: Omit<InsightEvaluation, "evaluatedAt" | "id">,
  ): Promise<InsightEvaluation> {
    const supabase = await getSupabaseClient();

    const databaseEvaluation = {
      confidence: evaluation.confidence,
      evaluator: evaluation.evaluator ?? "system",
      evidence: evaluation.evidence ?? {},
      insight_id: evaluation.insightId,
      insight_version: evaluation.insightVersion,
      notes: evaluation.notes ?? null,
      relevance: evaluation.relevance,
      still_valid: evaluation.stillValid,
    };

    const { data, error } = (await supabase
      .from("context_insight_evaluations")
      .insert(databaseEvaluation)
      .select()
      .single()) as {
      data: DatabaseEvaluationRow | null;
      error: null | PostgrestError;
    };

    if (error) {
      throw new Error(`Failed to create evaluation: ${error.message}`);
    }

    if (!data) {
      throw new Error("Failed to create evaluation: No data returned");
    }

    return {
      confidence: data.confidence,
      evaluatedAt: new Date(data.evaluated_at).getTime(),
      ...(data.evaluator && {
        evaluator: data.evaluator as "rule" | "system" | "user",
      }),
      evidence: data.evidence,
      id: data.id,
      insightId: data.insight_id,
      insightVersion: data.insight_version,
      ...(data.notes && { notes: data.notes }),
      relevance: data.relevance,
      stillValid: data.still_valid,
    };
  },
};
